/**
 * Aplica CSS variables de co-branding no documentElement.
 * Nunca altera --primary do tema Cork: isso quebra o dark mode até o reload.
 */

/**
 * @param {string|null|undefined} hex
 * @returns {string|null}
 */
export function normalizeBrandHex(hex) {
    if (typeof hex !== 'string') return null;
    const v = hex.trim();
    if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v)) return null;
    if (v.length === 4) {
        return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`.toLowerCase();
    }
    return v.toLowerCase();
}

/**
 * @param {string} hex
 * @returns {string|null} "r, g, b"
 */
export function hexToRgbTriplet(hex) {
    const n = normalizeBrandHex(hex);
    if (!n) return null;
    const full = n.slice(1);
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    if ([r, g, b].some((x) => Number.isNaN(x))) return null;
    return `${r}, ${g}, ${b}`;
}

/**
 * @param {{
 *   primary_color?: string|null,
 *   accent_color?: string|null,
 *   mode?: string|null,
 *   is_customized?: boolean,
 * }|null|undefined} branding
 * @param {HTMLElement} [root]
 */
export function applyBrandingCssVars(branding, root = document.documentElement) {
    if (!root || !branding || branding.is_customized === false) {
        clearBrandingCssVars(root);
        return;
    }

    const primary = normalizeBrandHex(branding.primary_color);
    const accent = normalizeBrandHex(branding.accent_color) || primary;
    if (!primary) {
        clearBrandingCssVars(root);
        return;
    }

    const primaryRgb = hexToRgbTriplet(primary);
    const accentRgb = hexToRgbTriplet(accent);

    root.style.setProperty('--brand-primary', primary);
    root.style.setProperty('--brand-accent', accent || primary);
    if (primaryRgb) root.style.setProperty('--brand-primary-rgb', primaryRgb);
    if (accentRgb) root.style.setProperty('--brand-accent-rgb', accentRgb);

    root.dataset.brandMode = branding.mode === 'white_label' ? 'white_label' : 'co_brand';
    root.classList.add('has-co-branding');
}

/**
 * @param {HTMLElement} [root]
 */
export function clearBrandingCssVars(root = document.documentElement) {
    if (!root) return;
    [
        '--brand-primary',
        '--brand-accent',
        '--brand-primary-rgb',
        '--brand-accent-rgb',
    ].forEach((k) => root.style.removeProperty(k));
    // Se builds antigas deixaram --primary inline, remove sem depender do tema Cork.
    if (root.style.getPropertyValue('--primary')) {
        root.style.removeProperty('--primary');
    }
    delete root.dataset.brandMode;
    root.classList.remove('has-co-branding');
}

/**
 * @returns {{ applyBrandingCssVars: typeof applyBrandingCssVars, clearBrandingCssVars: typeof clearBrandingCssVars }}
 */
export function useApplyBrandingCssVars() {
    return { applyBrandingCssVars, clearBrandingCssVars, normalizeBrandHex, hexToRgbTriplet };
}
